import { useEffect, useRef } from "react";

/**
 * Custom hook that scrolls to a dashboard when it becomes highlighted
 * This ensures scrolling happens after React re-renders the component
 */
export function useScrollToHighlighted(
  highlightedDashboardId: string | null,
  isScrolling: boolean = true
) {
  const previousHighlightedId = useRef<string | null>(null);

  useEffect(() => {
    // Only scroll when a new dashboard becomes highlighted (not when cleared)
    if (
      isScrolling &&
      highlightedDashboardId &&
      highlightedDashboardId !== previousHighlightedId.current
    ) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const attemptScroll = (attempt: number = 1, maxAttempts: number = 10) => {
          const selectors = [
            `tr[data-row-key="${highlightedDashboardId}"]`,
            `.ant-table-tbody tr[data-row-key="${highlightedDashboardId}"]`,
            `.dashboard-clone-highlight`
          ];

          let element: HTMLElement | null = null;

          for (const selector of selectors) {
            element = document.querySelector(selector) as HTMLElement;
            if (element) {
              break;
            }
          }

          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          } else if (attempt < maxAttempts) {
            setTimeout(() => attemptScroll(attempt + 1, maxAttempts), 200);
          }
        };

        attemptScroll();
      });
    }

    previousHighlightedId.current = highlightedDashboardId;
  }, [highlightedDashboardId, isScrolling]);
}
